import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtOdds } from "../lib/api";
import { UpgradeGate } from "../components/Bits";
import { Radio, TrendingUp, TrendingDown, Search } from "lucide-react";

export default function Odds() {
  const [q, setQ] = useState("");
  const { data } = useQuery({ queryKey: ["odds"], queryFn: () => api.get("/odds").then((r) => r.data) });
  const allRows = data?.rows || [];
  const rows = q.trim()
    ? allRows.filter((r) => `${r.name} ${r.team} ${r.team_name}`.toLowerCase().includes(q.trim().toLowerCase()))
    : allRows;
  const books = data?.books || [];
  const movers = allRows.filter((r) => r.move).sort((a, b) => Math.abs(b.move) - Math.abs(a.move)).slice(0, 6);

  return (
    <div className="space-y-5">
      <div className="fade-up">
        <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">Odds <span className="gold-text">Tracker</span></h1>
        <div className="flex items-center gap-2 mt-1">
          <p className="text-neutral-500 text-sm">HR props across sportsbooks. Gold = best available line. Sorted by value edge.</p>
          {data?.live && (
            <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#22c55e] border border-[#22c55e]/40 bg-[#22c55e]/10 px-2 py-0.5 rounded-full" data-testid="odds-live-badge">
              <Radio size={10} /> Live lines
            </span>
          )}
        </div>
      </div>

      {movers.length > 0 && (
        <div className="card-surface rounded-xl p-4 fade-up-1" data-testid="odds-movers-strip">
          <h3 className="font-display text-base font-bold uppercase mb-3">Line Movement <span className="text-[#a855f7]">· Biggest Movers</span></h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            {movers.map((r) => (
              <Link to={`/research/${r.mlb_id}`} key={r.mlb_id} data-testid={`mover-${r.mlb_id}`}
                className={`rounded-lg border p-2.5 hover-lift ${r.move < 0 ? "border-[#a855f7]/40 bg-[#a855f7]/5" : "border-[#22c55e]/30 bg-[#22c55e]/5"}`}>
                <div className="flex items-center gap-2">
                  <img src={r.headshot} alt="" className="w-7 h-7 rounded-full object-cover bg-white/5" />
                  <div className="text-xs font-semibold truncate">{r.name}</div>
                </div>
                <div className="mt-1.5 font-data text-xs flex items-center gap-1">
                  {r.move < 0 ? <TrendingDown size={12} className="text-[#a855f7]" /> : <TrendingUp size={12} className="text-[#22c55e]" />}
                  <span className="text-neutral-500">{fmtOdds(r.prev_odds)}</span>
                  <span className="text-neutral-600">→</span>
                  <span className={`font-bold ${r.move < 0 ? "text-[#a855f7]" : "text-[#22c55e]"}`}>{fmtOdds(r.best_odds)}</span>
                </div>
                <div className={`text-[9px] uppercase tracking-widest font-bold mt-1 ${r.move < 0 ? "text-[#a855f7]" : "text-[#22c55e]"}`}>
                  {r.move < 0 ? "Steam · market backing" : "Drifting · better payout"}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="relative max-w-md fade-up-1">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a855f7]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a player or team..." data-testid="odds-search-input"
          className="w-full bg-[#121212] border border-white/15 focus:border-[#a855f7] rounded-lg pl-9 pr-3 py-2.5 text-sm outline-none transition-colors duration-200" />
      </div>

      <div className="card-surface rounded-xl overflow-x-auto fade-up-1">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="text-neutral-500 text-[11px] uppercase tracking-wider text-left border-b border-white/10">
              <th className="p-3">Player — To Hit a HR</th>
              {books.map((b) => <th key={b} className="p-3 text-center">{b}</th>)}
              <th className="p-3 text-center">Move</th>
              <th className="p-3 text-center">Model</th>
              <th className="p-3 text-center">Edge</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const byBook = Object.fromEntries(r.lines.map((l) => [l.book, l]));
              return (
                <tr key={r.mlb_id} className="border-b border-white/5 hover:bg-[#1a1a1a] transition-colors duration-200" data-testid={`odds-row-${r.mlb_id}`}>
                  <td className="p-3">
                    <Link to={`/research/${r.mlb_id}`} className="flex items-center gap-2.5 group">
                      <img src={r.headshot} alt="" className="w-8 h-8 rounded-full object-cover bg-white/5" />
                      <div>
                        <div className="font-medium group-hover:text-[#a855f7] transition-colors duration-200">
                          {r.name}
                          {r.source === "the_odds_api" ? (
                            <span className="ml-2 text-[9px] uppercase tracking-widest font-bold text-[#22c55e]" title="Live sportsbook line">live</span>
                          ) : (
                            <span className="ml-2 text-[9px] uppercase tracking-widest font-bold text-neutral-600" title="Estimated line — book hasn't posted yet">est.</span>
                          )}
                        </div>
                        <div className="text-[11px] text-neutral-600">{r.team}</div>
                      </div>
                    </Link>
                  </td>
                  {books.map((b) => {
                    const l = byBook[b];
                    return (
                      <td key={b} className="p-3 text-center">
                        {l ? (
                          <span className={`font-data inline-block px-2 py-1 rounded ${b === r.best_book ? "border border-[#a855f7]/60 text-[#a855f7] font-bold bg-[#a855f7]/10" : "text-neutral-300"}`}>
                            {fmtOdds(l.odds)}
                          </span>
                        ) : (
                          <span className="text-neutral-700">—</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="p-3 text-center" data-testid={`odds-move-${r.mlb_id}`}>
                    {r.move ? (
                      <span className={`font-data text-xs font-bold inline-flex items-center gap-1 ${r.move < 0 ? "text-[#a855f7]" : "text-[#22c55e]"}`}>
                        {r.move < 0 ? <TrendingDown size={12} /> : <TrendingUp size={12} />}
                        {fmtOdds(r.prev_odds)}→{fmtOdds(r.best_odds)}
                      </span>
                    ) : r.move === 0 ? (
                      <span className="text-neutral-600 font-data text-xs">flat</span>
                    ) : (
                      <span className="text-neutral-700">—</span>
                    )}
                  </td>
                  <td className="p-3 text-center font-data text-neutral-300">{r.model_prob}%</td>
                  <td className={`p-3 text-center font-data font-bold ${r.edge > 0 ? "text-[#22c55e]" : "text-red-400"}`}>{r.edge > 0 ? "+" : ""}{r.edge}%</td>
                </tr>
              );
            })}
            {rows.length === 0 && q.trim() && (
              <tr><td colSpan={books.length + 4} className="p-4 text-neutral-500 text-sm" data-testid="odds-search-empty">No player matches "{q}".</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {data?.locked && <UpgradeGate feature={`The full odds board (${data.total} players tracked)`} />}
    </div>
  );
}

